import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";
import * as qs from "qs";

import { defined } from "@formant/core/common/defined";
import { IOrganization } from "@formant/core/model/IOrganization";
import { HStack } from "@formant/ui/component/common/HStack";
import { TextField } from "@formant/ui/component/common/TextField";
import { VStack } from "@formant/ui/component/common/VStack";

import {
    awsAccountId,
    googleClientId,
    pagerdutyAppId,
    slackClientId,
} from "../../../config";
import googleSvg from "../../../static/images/google.svg";
import pagerdutySvg from "../../../static/images/pagerduty.svg";
import s3Svg from "../../../static/images/s3.svg";
import slackSvg from "../../../static/images/slack.svg";
import {
    appStore,
    authStore,
    organizationStore,
    routerStore,
} from "../../../stores";
import { Help } from "../../common/Help";
import { Table } from "../../common/Table";
import { TextButton } from "../../common/TextButton";
import { AddButton } from "../../configuration/AddButton";
import { EditAwsInfo } from "./EditAwsInfo";
import { EditGcsInfo } from "./EditGcsInfo";
import { IntegrationRow } from "./IntegrationRow";
import * as styles from "./IntegrationsSection.scss";
import { SlackWebhooksRow } from "./SlackWebhooksRow";

export interface IIntegrationsSectionProps {
    organization: IOrganization;
}

// s3-export-worker (controls both S3 and GCP exports) is disabled for now to save on costs
const globalDataExportEnabled = false;

@observer
export class IntegrationsSection extends Component<IIntegrationsSectionProps> {
    @observable
    private editingGcsInfo: boolean = false;

    @observable
    private editingAwsInfo: boolean = false;

    public render() {
        const { organization } = this.props;
        const impersonating = !!authStore.impersonateUserId;
        const { awsInfo } = organization;
        const { routeTo } = routerStore;

        const showS3Export =
            globalDataExportEnabled &&
            awsAccountId &&
            organizationStore.dataExportEnabled() &&
            organizationStore.s3ExportEnabled() &&
            authStore.featureEnabled("s3Export");

        // TODO: gcsExport
        const showGCPExport =
            globalDataExportEnabled &&
            organizationStore.dataExportEnabled() &&
            organizationStore.googleStorageExportEnabled();

        const showGoogleSheets = organizationStore.dataExportEnabled();

        const slackWebhooks = (organization.slackInfo || {}).webhooks || [];
        const slackWebhooksRows = slackWebhooks.map((_, index) => (
            <SlackWebhooksRow
                key={index}
                webhook={_}
                index={index}
                onDelete={() => this.onRemoveSlack(index)}
            />
        ));

        return (
            <VStack className={styles["integrations-section"]}>
                {showS3Export && (
                    <IntegrationRow
                        logoSrc={s3Svg}
                        name="AWS S3"
                        description="Upload Formant data to AWS S3"
                        actions={
                            <>
                                {!awsInfo && (
                                    <TextButton
                                        data-testid="aws-connect-button"
                                        className={styles.connect}
                                        onClicked={this.onConnectAws}
                                        disabled={impersonating}
                                    >
                                        Connect
                                    </TextButton>
                                )}
                                {awsInfo && (
                                    <TextButton
                                        onClicked={this.onRemoveAws}
                                        data-testid="aws-disconnect-button"
                                    >
                                        Disconnect
                                    </TextButton>
                                )}
                                <Help
                                    text="Continuously upload Formant telemetry data to Amazon S3 for archive and downstream analysis"
                                    to="https://docs.formant.io/docs/integrations-overview#aws-s3-integration"
                                />
                            </>
                        }
                    >
                        {awsInfo && (
                            <HStack className={styles.info}>
                                <TextField
                                    label="Account ID"
                                    value={awsInfo.accountId}
                                    readOnly={true}
                                />
                                <TextField
                                    label="Role name"
                                    value={awsInfo.roleName}
                                    readOnly={true}
                                />
                                <TextField
                                    label="Bucket"
                                    value={
                                        awsInfo.s3Export &&
                                        awsInfo.s3Export.bucket
                                    }
                                    readOnly={true}
                                />
                                <TextField
                                    label="Prefix"
                                    value={
                                        awsInfo.s3Export &&
                                        awsInfo.s3Export.path
                                    }
                                    readOnly={true}
                                />
                            </HStack>
                        )}
                        {awsAccountId && this.editingAwsInfo && (
                            <EditAwsInfo
                                organizationId={defined(organization.id)}
                                externalId={defined(organization.externalId)}
                                awsInfo={
                                    awsInfo || {
                                        accountId: "",
                                        roleName: "",
                                        s3Export: {
                                            bucket: "",
                                        },
                                    }
                                }
                                awsAccountId={awsAccountId}
                                onClose={this.onCloseAwsInfo}
                            />
                        )}
                    </IntegrationRow>
                )}
                {showGCPExport && (
                    <IntegrationRow
                        logoSrc={googleSvg}
                        name="GCP Storage"
                        description="Upload Formant data to Google Cloud Storage"
                        actions={
                            <>
                                {!organization.googleStorageInfo && (
                                    <TextButton
                                        className={styles.connect}
                                        onClicked={this.onConnectGcs}
                                        disabled={impersonating}
                                        data-testid="gcp-connect-button"
                                    >
                                        Connect
                                    </TextButton>
                                )}
                                {organization.googleStorageInfo && (
                                    <TextButton
                                        onClicked={this.onRemoveGcs}
                                        data-testid="gcp-disconnect-button"
                                    >
                                        Disconnect
                                    </TextButton>
                                )}
                                <Help
                                    text="Continuously upload Formant telemetry data to Google Cloud Storage for archive and downstream analysis"
                                    to="https://docs.formant.io/docs/integrations-overview#gcp-integration"
                                />
                            </>
                        }
                    >
                        {organization.googleStorageInfo && (
                            <div className={styles.info}>
                                <TextField
                                    label="Bucket"
                                    value={
                                        organization.googleStorageInfo
                                            .googleStorageExport?.bucket
                                    }
                                    readOnly={true}
                                />
                                <TextField
                                    label="Prefix"
                                    value={
                                        organization.googleStorageInfo
                                            .googleStorageExport?.path
                                    }
                                    readOnly={true}
                                />
                            </div>
                        )}
                        {this.editingGcsInfo && (
                            <EditGcsInfo
                                organizationId={defined(organization.id)}
                                gcsInfo={
                                    organization.googleStorageInfo || {
                                        credentialsJsonKey: "",
                                        googleStorageExport: {
                                            bucket: "",
                                        },
                                    }
                                }
                                onClose={this.onCloseGcsInfo}
                            />
                        )}
                    </IntegrationRow>
                )}
                {pagerdutyAppId && (
                    <IntegrationRow
                        logoSrc={pagerdutySvg}
                        name="PagerDuty"
                        description="Send Formant events to PagerDuty"
                        actions={
                            <>
                                {!organization.pagerdutyInfo && (
                                    <TextButton
                                        data-testid="pagerduty-connect-button"
                                        className={styles.connect}
                                        to={`https://app.pagerduty.com/install/integration?${qs.stringify(
                                            {
                                                version: "2",
                                                app_id: pagerdutyAppId,
                                                redirect_url: `${
                                                    window.location.origin
                                                }${routeTo.pagerdutyIntegration()}`,
                                            },
                                        )}`}
                                        disabled={impersonating}
                                    >
                                        Connect
                                    </TextButton>
                                )}
                                {organization.pagerdutyInfo && (
                                    <TextButton
                                        onClicked={this.onRemovePagerduty}
                                        data-testid="pagerduty-disconnect-button"
                                    >
                                        Disconnect
                                    </TextButton>
                                )}
                                <Help
                                    text="Formant events are sent to PagerDuty for realtime actionability"
                                    to="https://docs.formant.io/docs/send-events-to-pagerduty"
                                />
                            </>
                        }
                    >
                        {organization.pagerdutyInfo && (
                            <div className={styles.info}>
                                <TextField
                                    label="Account"
                                    className={styles.field}
                                    value={organization.pagerdutyInfo.account}
                                    readOnly={true}
                                />
                                <TextField
                                    label="Service name"
                                    className={classnames(
                                        styles.field,
                                        styles.large,
                                    )}
                                    value={
                                        organization.pagerdutyInfo.serviceName
                                    }
                                    readOnly={true}
                                />
                            </div>
                        )}
                    </IntegrationRow>
                )}
                {showGoogleSheets && googleClientId && (
                    <IntegrationRow
                        logoSrc={googleSvg}
                        name="Google Sheets"
                        description="Read and write Google Sheets"
                        actions={
                            <>
                                {!organization.googleInfo && (
                                    <TextButton
                                        data-testid="googlesheet-connect-button"
                                        className={styles.connect}
                                        to={`https://accounts.google.com/o/oauth2/v2/auth?${qs.stringify(
                                            {
                                                client_id: googleClientId,
                                                redirect_uri:
                                                    window.location.origin +
                                                    routeTo.googleIntegration(),
                                                response_type: "code",
                                                scope: "https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive",
                                                access_type: "offline",
                                                prompt: "consent",
                                            },
                                        )}`}
                                        disabled={impersonating}
                                    >
                                        Connect
                                    </TextButton>
                                )}
                                {organization.googleInfo && (
                                    <TextButton
                                        onClicked={this.onRemoveGoogle}
                                        data-testid="googlesheet-disconnect-button"
                                    >
                                        Disconnect
                                    </TextButton>
                                )}
                                <Help
                                    text="Export Formant events to Google Sheets, and annotate rows from Google Sheets"
                                    to="https://docs.formant.io/docs/integrate-with-google-sheets"
                                />
                            </>
                        }
                    />
                )}
                {slackClientId && (
                    <>
                        <IntegrationRow
                            logoSrc={slackSvg}
                            name="Slack"
                            description="Read and write Slack channels"
                            actions={
                                <>
                                    <AddButton
                                        data-testid="slack-connect-button"
                                        className={styles.connect}
                                        label="Connect"
                                        onClicked={this.onAddSlack}
                                        count={slackWebhooksRows.length}
                                        max={20}
                                        tooltips={[
                                            "Connect slack",
                                            "Maximum 20 slack channels",
                                        ]}
                                    />
                                    <Help
                                        text="Send Formant events to a Slack channel for realtime notifications, and ingest Slack conversations for integrated analysis with telemetry"
                                        to="https://docs.formant.io/docs/send-events-to-slack"
                                    />
                                </>
                            }
                        />
                        {slackWebhooksRows.length > 0 && (
                            <Table>
                                <tbody>{slackWebhooksRows}</tbody>
                            </Table>
                        )}
                    </>
                )}
            </VStack>
        );
    }

    private onCloseGcsInfo = () => {
        this.editingGcsInfo = false;
    };

    private onConnectGcs = () => {
        this.editingGcsInfo = true;
    };

    private onRemoveGcs = () => {
        appStore.setConfirm({
            title: "Delete",
            message: "Delete Google Cloud Storage integration?",
            onOk: () =>
                organizationStore.update(defined(this.props.organization.id), {
                    googleStorageInfo: false,
                }),
        });
    };

    private onCloseAwsInfo = () => {
        this.editingAwsInfo = false;
    };

    private onConnectAws = () => {
        this.editingAwsInfo = true;
    };

    private onRemoveAws = () => {
        appStore.setConfirm({
            title: "Delete",
            message: "Delete AWS S3 integration?",
            onOk: () =>
                organizationStore.update(defined(this.props.organization.id), {
                    awsInfo: false,
                }),
        });
    };

    private onRemoveGoogle = () => {
        appStore.setConfirm({
            title: "Delete",
            message: "Delete Google Sheets integration?",
            onOk: () =>
                organizationStore.update(defined(this.props.organization.id), {
                    googleInfo: false,
                }),
        });
    };

    private onRemovePagerduty = () => {
        appStore.setConfirm({
            title: "Delete",
            message: "Delete PagerDuty integration?",
            onOk: () =>
                organizationStore.update(defined(this.props.organization.id), {
                    pagerdutyInfo: false,
                }),
        });
    };

    private onAddSlack = () => {
        const { routeTo } = routerStore;
        window.open(
            `https://slack.com/oauth/authorize?${qs.stringify({
                client_id: slackClientId,
                scope: "incoming-webhook,bot,users:read,channels:read,files:read",
                redirect_uri:
                    window.location.origin + routeTo.slackIntegration(),
            })}`,
        );
    };

    private onRemoveSlack = (webhookIndex: number) => {
        const slackInfo = this.props.organization.slackInfo || {};
        const newWebhooks = slackInfo.webhooks ? [...slackInfo.webhooks] : [];
        newWebhooks.splice(webhookIndex, 1);

        appStore.setConfirm({
            title: "Delete",
            message: "Delete Slack integration?",
            onOk: () =>
                organizationStore.update(defined(this.props.organization.id), {
                    slackInfo: {
                        webhooks: newWebhooks,
                    },
                }),
        });
    };
}
